import {
  ChangeDetectionStrategy,
  Component,
  ViewChild,
  inject,
  input,
} from '@angular/core';
import {DatePipe, NgClass} from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { DatePicker } from 'primeng/datepicker';
import { InputText } from 'primeng/inputtext';
import { Paginator } from 'primeng/paginator';
import { Select } from 'primeng/select';
import { Table, TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import { TableBaseAbstractComponent } from '../../../../../../../shared/components/table-base-abstract.component';
import { AdminPanelCorrespondenceStatus } from '../../../../../../../shared/enums/admin-panel-correspondence-status.enum';
import { AdminPanelUserDetails } from '../../../../../../../shared/models/admin-panel-user-details';
import { AdminPanelUserCorrespondenceEntry } from '../../../../../../../shared/models/admin-panel-user-correspondence-entry';
import { TableRequestData } from '../../../../../../../shared/models/table/table-request-param';
import { TableResponse } from '../../../../../../../shared/models/table-response';
import { ConstLabelReaderPipe } from '../../../../../../../shared/pipes/const-label-reader.pipe';
import { AdminPanelUsersApi } from '../../../../../../../shared/services/api/admin-panel-users.api';
import { AdminPanelUserCorrespondenceStatusBadgeClassPipe } from './pipe/admin-panel-user-correspondence-status-badge-class.pipe';
import { TableScrollWrapperComponent } from '../../../../../../../shared/components/table-scroll-wrapper/table-scroll-wrapper.component';

@Component({
  selector: 'app-admin-panel-user-correspondence-table',
  imports: [
    Button,
    Card,
    ConstLabelReaderPipe,
    DatePicker,
    FormsModule,
    InputText,
    Paginator,
    Select,
    TableModule,
    Tooltip,
    DatePipe,
    AdminPanelUserCorrespondenceStatusBadgeClassPipe,
    NgClass,
    TableScrollWrapperComponent,
  ],
  templateUrl: './admin-panel-user-correspondence-table.component.html',
  styleUrl: './admin-panel-user-correspondence-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminPanelUserCorrespondenceTableComponent extends TableBaseAbstractComponent<AdminPanelUserCorrespondenceEntry> {
  @ViewChild('table') override tableRef!: Table;

  readonly #adminPanelUsersApi = inject(AdminPanelUsersApi);

  userDetails = input.required<AdminPanelUserDetails>();

  protected readonly statusButtons: Array<{
    label: string;
    value: keyof typeof AdminPanelCorrespondenceStatus;
  }> = (
    Object.entries(AdminPanelCorrespondenceStatus) as Array<
      [keyof typeof AdminPanelCorrespondenceStatus, string]
    >
  ).map(([key, value]) => ({
    label: value,
    value: key,
  }));
  protected readonly archiwalnyOptions = [
    { label: 'Tak', value: true },
    { label: 'Nie', value: false },
  ];

  protected tableFilters = {
    nazwaWniosku: '',
    status: null as keyof typeof AdminPanelCorrespondenceStatus | null,
    archiwalny: null as boolean | null,
    adresatLubNadawca: '',
    numerDokumentu: '',
    dataUtworzenia: null as Date | null,
    dataWyslaniaLubNadania: null as Date | null,
    dataPoswiadczenia: null as Date | null,
  };

  constructor() {
    super();
    this.tableRequest.update((value) => ({
      ...value,
      pageSize: 10,
    }));
  }

  protected override apiCall$ = (
    params: TableRequestData,
  ): Observable<TableResponse<AdminPanelUserCorrespondenceEntry>> => {
    return this.#adminPanelUsersApi.getUserCorrespondence$(
      this.userDetails(),
      params,
    );
  };

  protected onFilterChangeMobile(
    target: EventTarget | null,
    field: string,
  ): void {
    this.onPrimeFilterChangeMobile(target, field);
  }

  protected isStatusActive(
    status: keyof typeof AdminPanelCorrespondenceStatus,
  ): boolean {
    return this.tableRequest().filters?.['status'] === status;
  }

  protected selectStatus(
    status: keyof typeof AdminPanelCorrespondenceStatus,
  ): void {
    const nextStatus = this.isStatusActive(status) ? null : status;
    this.tableFilters.status = nextStatus;

    this.tableRequest.update((tableRequestData) => ({
      ...tableRequestData,
      pageIndex: 0,
      filters: {
        ...tableRequestData.filters,
        status: nextStatus,
      },
    }));
  }

  protected onStatusFilterChange(
    status: keyof typeof AdminPanelCorrespondenceStatus | null,
  ): void {
    this.tableFilters.status = status;
    this.onPrimeFilterChange(status, 'status');
  }

  protected getSelectedStatusLabel(): string | undefined {
    return (
      this.statusButtons.find(
        ({ value }) => value === this.tableFilters.status,
      )?.label ?? 'Wybierz status'
    );
  }

  protected getSelectedArchiwalnyLabel(): string | undefined {
    return (
      this.archiwalnyOptions.find(
        ({ value }) => value === this.tableFilters.archiwalny,
      )?.label ?? 'Wybierz wartość'
    );
  }

  protected clearFilters(): void {
    this.tableFilters = {
      nazwaWniosku: '',
      status: null,
      archiwalny: null,
      adresatLubNadawca: '',
      numerDokumentu: '',
      dataUtworzenia: null,
      dataWyslaniaLubNadania: null,
      dataPoswiadczenia: null,
    };
    this.resetFilters();
  }

  protected readonly AdminPanelCorrespondenceStatus = AdminPanelCorrespondenceStatus;
}
